import { isEqual } from 'lodash';
import queryString from 'querystring';
import React, { Component } from 'react';

import analyticService from '../../../../services/analytics';
import { session } from '../../../../services/session';
import { Loader, NoResult } from '../../../misc/loader-utils';
import productService from '../product-service';

import CartSiblingsModal from './cart-siblings-modal';
import Pagination from '../../../base/common/pagination';
import Product from '../cart-explorer/product';
import { getProductsData } from '../cart-explorer/cart-explorer-mocks';

class ProductResearch extends Component {
  constructor(props) {
    super(props);

    this.AnalyticService = analyticService;
    this.service = productService;
    this.Session = session;

    this.logoMap = {};

    this.headerText = {
      all: 'What items are best sellers?',
      new: 'What are new products?',
      sale: 'What items are on sale that have generated the biggest carts?',
    };
    this.table = null;

    this.state = {
      products: {
        '30d': [],
        '24h': [],
      },
      selectedProductIdx: null,

      // Pagination
      page: 1,
      perPage: this.service.paginationOptions[2],
      totalItems: 0,

      loading: true,
      showNoResult: false,
    };
  }

  componentDidMount() {
    const updatedState = this.getStateFromQueryString();
    this.props.merchants.forEach(merchant => {
      this.logoMap[merchant.merch_id] = merchant.logo_url;
    });

    this.setState(updatedState, () => {
      // setQueryString() fills out default values if they aren't set
      this.setQueryString();

      this.queryCartSummary();
      this.queryBestSellers();
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const prevMerchIds = queryString.parse(prevProps.urlQuery).merch_ids;
    const currentMerchIds = queryString.parse(this.props.urlQuery).merch_ids;
    const searchChange = prevProps.searchTerm !== this.props.searchTerm;

    if (
      searchChange ||
      prevProps.currentTab !== this.props.currentTab ||
      !isEqual(prevMerchIds, currentMerchIds) ||
      !isEqual(prevProps.sortOptions, this.props.sortOptions)
    ) {
      // Reset page when user selects a new merchant, search, or sort
      this.setState(
        {
          page: 1,
          selectedProductIdx: null,
          loading: true, // Set loading here to prevent cart component from unmounting
        },
        () => {
          this.setQueryString({ page: 1 });
          this.queryBestSellers(true).then(() => {
            // Reset scroll when merchant changes
            this.table.scrollTop = 0;
          });

          // Only re-query totals on search
          if (searchChange) this.queryCartSummary();
        },
      );
    }

    /*
     * Display loader when user selects a new merchant via carousel
     * to prevent showing debounce delay to clients
     */
    if (!isEqual(prevProps.merchants, this.props.merchants)) {
      this.setState({ loading: true });
    }

    if (!isEqual(prevProps.currentDateRange, this.props.currentDateRange)) {
      this.setState(
        {
          currentDateRange: this.props.currentDateRange,
          loading: true,
        },
        () => {
          this.setQueryString();
          this.queryCartSummary();
          this.queryBestSellers();
        },
      );
    }
  }

  /* Params handling */
  getStateFromQueryString() {
    // Preserve table state by getting params from the query string
    const searchParams = new URLSearchParams(this.props.location.search);
    return {
      page: parseInt(searchParams.get('page')) || this.state.page,
      perPage: parseInt(searchParams.get('per_page')) || this.state.perPage,
    };
  }

  setQueryString() {
    /*
     * ProductResearch and SearchTrends are responsible for adding
     * to the query string params that are important to them,
     * using a helper function from Insights.
     */
    const params = this.getRequestParams();
    Object.assign(params, {
      page: this.state.page,
      per_page: this.state.perPage,
      tab: this.props.currentTab,
    });

    this.props.setQueryString(params);
  }

  queryCartSummary() {
    this.props.updateProductData({});

    const params = this.getRequestParams(true);
    return this.service.queryCartSummary(this.Session.orgId, params).then(totals => {
      this.props.updateProductData(totals);
    });
  }

  queryBestSellers(resetPages = false) {
    this.setState({
      loading: true,
      totalItems: 0,
    });

    const params = this.getRequestParams();
    const { merchIds } = this.props;
    const dateRange = this.props.currentDateRange;
    return this.service
      .getProductTrends(
        this.Session.orgId,
        merchIds,
        params,
        this.state.page,
        this.state.perPage,
        dateRange,
        resetPages,
      )
      .then(products => {
        if (!products.length) {
          this.setState({ showNoResult: true, loading: false });
          return;
        }

        const totalItems = this.service.productsByMerch[merchIds][dateRange].total_items;

        const bestSellerProducts = { ...this.state.products };
        bestSellerProducts[dateRange] = products;

        this.setState({
          products: bestSellerProducts,
          loading: false,
          showNoResult: false,
          totalItems,
        });
      });
  }

  getRequestParams(isTotal = false) {
    // Return query params for the best seller request
    const params = {
      date_from: this.props.params.date_from,
      date_to: this.props.params.date_to,
      merch_ids: this.props.merchIds,
      search_term: this.props.searchTerm || null,
    };

    if (this.props.currentTab !== 'all') {
      params.product_type = this.props.currentTab;
    }

    if (isTotal) {
      // Always query cart summary for all merchants
      const merchIds = this.props.merchants
        .filter(merch => !merch.hidden)
        .map(merch => merch.merch_id)
        .join(',');
      Object.assign(params, { merch_ids: merchIds });
    } else {
      const sortOption = this.props.sortOptions.filter(option => option.selected)[0];
      Object.assign(params, {
        order_by: sortOption.name,
        direction: sortOption.direction,
      });
    }

    return params;
  }

  /* Actions */
  updateState(name) {
    // Used to update page, perPage, sortOptions
    return value => {
      this.setState(
        {
          [name]: value,
          selectedProductIdx: null,
        },
        () => {
          this.setQueryString();
          this.queryBestSellers().then(() => {
            this.table.scrollTop = 0;
          });
        },
      );
    };
  }

  selectProduct(index) {
    const productName = this.state.products[this.props.currentDateRange][index].product_name;
    this.AnalyticService.trackEvent(
      'Product Research',
      'Click',
      `Best Seller Product ${productName}`,
    );

    this.setState({ selectedProductIdx: index });
  }

  trackLinkOut(url) {
    const gaLabel = `Hyperlink on Best Seller Product ${url}`;
    this.AnalyticService.trackEvent('Product Research', 'Click', gaLabel);
  }

  setMockData() {
    const mockProducts = getProductsData(this.state.params)[0].stats;
    this.setState({
      products: {
        [this.props.currentDateRange]: mockProducts,
      },
      showNoResult: false,
      totalItems: mockProducts.length,
    });
  }

  render() {
    const scrollable = this.props.scrollable ? 'scrollable' : '';
    const selectedProduct = this.state.selectedProductIdx;

    const products =
      !this.state.loading && !this.state.showNoResult
        ? this.state.products[this.props.currentDateRange].map((product, i) => {
            const merchant = this.props.merchants
              .filter(merch => merch.selected)
              .find(merch => {
                return merch.merch_id === product.merch_id;
              });
            return (
              <Product
                hiddenBorder={selectedProduct - 1 === i}
                key={i}
                merchant={merchant}
                product={product}
                selected={selectedProduct === i}
                selectProduct={() => this.selectProduct(i)}
                trackLinkOut={url => this.trackLinkOut(url)}
                currentTab={this.props.currentTab}
                type="product-research"
              />
            );
          })
        : null;

    // TODO: Show cart in modal
    const siblingsModal =
      this.state.selectedProductIdx !== null ? (
        <CartSiblingsModal
          logoMap={this.logoMap}
          params={this.props.params}
          product={this.state.products[this.props.currentDateRange][selectedProduct]}
          pubId={this.Session.orgId}
          scrollable={scrollable}
          trackLinkOut={url => this.trackLinkOut(url)}
          currentDateRange={this.props.currentDateRange}
          closeModal={() => this.setState({ selectedProductIdx: null })}
        />
      ) : null;

    const mockButton = session.isAdminView ? (
      <button className="mocks-btn" onClick={this.setMockData.bind(this)}>
        Mocks
      </button>
    ) : null;

    return (
      <div className="product-research">
        <div className="cart-container">
          <div className={`best-sellers ${this.props.currentTab}`}>
            <div className="header">{mockButton}</div>
            <div className={`best-sellers-table ${scrollable}`} ref={node => (this.table = node)}>
              <Loader loading={this.state.loading} />
              <NoResult
                type={this.props.noResult}
                noResult={!this.state.loading && this.state.showNoResult}
              />
              {products}
            </div>
            <div className="footer">
              <Pagination
                showPagination={!this.state.showNoResult}
                currentPage={this.state.page}
                totalItems={this.state.totalItems}
                perPage={this.state.perPage}
                eventPrefix="Product Research"
                updatePage={this.updateState('page')}
                updatePerPage={this.updateState('perPage')}
              />
            </div>
          </div>
        </div>
        {siblingsModal}
      </div>
    );
  }
}

export default ProductResearch;
